import { CSSProperties, FC, useCallback, useEffect, useState } from 'react';

import { CaretRightOutlined, ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Checkbox,
  Collapse,
  CollapseProps,
  Divider,
  Empty,
  Select,
  SelectProps,
  Spin,
  Tag,
  message,
  theme,
} from 'antd';
import { Modal as antModal } from 'antd';
import { Button as AntBtn } from 'antd';
import ColorPicker, { Color } from 'antd/es/color-picker';
import Pagination from 'component/pagination';
import tinycolor from 'tinycolor2';
import { useDebounce } from 'tools/hooks/debounce.hook';
import { Button, Dropdown, Form, Icon, Input, Label, Modal, Typography } from 'ui';
import BackLink from 'ui/BackLink';
import { formRules } from 'utils/formRules';

import {
  useCreateReferrerLinkMutation,
  useCreateReferrersGroupMutation,
  useDeleteReferrerItemMutation,
  useDeleteReferrersGroupMutation,
  useEditReferrerLinkMutation,
  useEditReferrersGroupMutation,
  useLazyGetReferrerItemQuery,
  useLazyGetReferrersGroupsQuery,
  useLazyGetReferrersListQuery,
} from 'services/promoter/promoterApiService';
import { useLazyGetCurrentUserQuery } from 'services/user-management/userManagementApiService';

import EventsLoadingState from 'containers/events-loading-state';

import {
  ReferrerEnum,
  ReferrerGroupEditReqType,
  ReferrerGroupReqType,
  ReferrerReqType,
} from 'types/promoters/promoterTypes';

import s from './ReferralTeam.module.scss';

const { confirm } = antModal;

const initialFilter: ReferrerReqType = {
  PageNumber: 1,
  PageSize: 10,
  Term: '',
  Type: ReferrerEnum.ALL,
  PromoterId: '',
};

const DROPDOWN_ITEMS = () => [
  { key: 'edit', label: <div className={s.dropdownItem}>Edit</div> },
  { key: 'remove', label: <div className={s.dropdownItem}>Remove</div> },
];

const GROUP_DROPDOWN_ITEMS = () => [
  { key: 'edit', label: <div className={s.dropdownItem}>Edit</div> },
  { key: 'remove', label: <div className={s.dropdownItem}>Remove</div> },
];

const ReferralTeam: FC = () => {
  const { token } = theme.useToken();

  const [form] = Form.useForm();
  const [groupForm] = Form.useForm();

  const panelStyle: React.CSSProperties = {
    marginBottom: 24,
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    border: 'none',
  };
  const [getReferrersList, { data: referrersList, isFetching }] = useLazyGetReferrersListQuery();
  const [getCurrentUser, { data: currentUser }] = useLazyGetCurrentUserQuery();
  const [editReferrerList, { isLoading: isEditingReferrerList }] = useEditReferrerLinkMutation();
  const [deleteReferrerItem] = useDeleteReferrerItemMutation();
  const [getReferrerItem] = useLazyGetReferrerItemQuery();
  const [colorString, setColorString] = useState<string>();
  const [CreateReferrerLink, { isLoading: isSending }] = useCreateReferrerLinkMutation();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isGroupEditing, setIsGroupEditing] = useState<boolean>(false);

  const handleChange = (values: string[]) => {
    setSelectedItems(values);
  };

  const [requestFilter, setRequestFilter] = useState<ReferrerReqType>(initialFilter);
  const [getReferrersGroups, { data: referrerGroups }] = useLazyGetReferrersGroupsQuery();
  const [createReferrersGroup, { isLoading: isGrouping }] = useCreateReferrersGroupMutation();
  const [editReferrerGroup, { isLoading: isEditingGroup }] = useEditReferrersGroupMutation();
  const [deleteReferrersGroup] = useDeleteReferrersGroupMutation();

  const [term, setTerm] = useState<string>('');
  const [type, setType] = useState<ReferrerEnum>(ReferrerEnum.ALL);
  const [promoterId, setPromoterId] = useState<string>();

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [loadingKeys, setLoadingKeys] = useState<string[]>([]);
  const [childData, setChildData] = useState<{ [key: string]: any }>({});
  const debouncedSearchEventName = useDebounce(term, 500) as string;

  const [open, setOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState<boolean>(false);

  const promoterOptions: SelectProps['options'] = currentUser?.promoters?.map((item) => ({
    label: item.name,
    value: item.promoterId,
  }));

  const [openedDropdown, setOpenedDropdown] = useState<string>();

  // Handle checkbox selection
  const handleCheckboxChange = (checked: boolean, value: string) => {
    setSelectedItems((prev) =>
      checked ? [...prev, value] : prev.filter((item) => item !== value),
    );
  };

  const [openedGroupDropdown, setOpenedGroupDropdown] = useState<string>();

  const showConfirm = useCallback(
    async (id: string) => {
      confirm({
        title: 'Do you want to delete this referrer?',
        icon: <ExclamationCircleOutlined />,
        content: 'This action cannot be undone.',
        centered: true,
        mask: false,
        async onOk() {
          const res = await deleteReferrerItem({ id });

          if ('data' in res) {
            message.success('Referrer removed successfully.');
            if (requestFilter?.PromoterId) {
              getReferrersList(requestFilter);
            }
          }
        },
        onCancel() {
          console.log('Cancel');
        },
      });
    },
    [deleteReferrerItem, getReferrersList, requestFilter], // Dependencies
  );

  const showGroupConfirm = useCallback(
    async (id: string) => {
      confirm({
        title: 'Do you want to delete this group?',
        icon: <ExclamationCircleOutlined />,
        content: 'This action cannot be undone.',
        centered: true,
        mask: false,
        async onOk() {
          const res = await deleteReferrersGroup({ id });

          if ('data' in res) {
            message.success('Referrer group removed successfully.');
            if (promoterId) {
              getReferrersGroups({ id: promoterId });
            }
          }
        },
        onCancel() {
          console.log('Cancel');
        },
      });
    },
    [deleteReferrersGroup, getReferrersGroups, promoterId], // Dependencies
  );

  const handleItemClick = useCallback(
    (ev: { key: string; id: string }) => {
      const referrer = referrersList?.referrers?.find((item) => item.id === ev.id);
      if (ev.key === 'edit') {
        setIsEditing(true);
        setOpen(true);
        form.resetFields();
        form.setFieldValue('email', referrer?.user?.userEmail);
        form.setFieldValue('referrerLink', referrer?.referrerLink);
        form.setFieldValue('promoterId', promoterId);
        form.setFieldValue('id', ev.id);
      } else if (ev.key === 'remove') {
        showConfirm(ev.id);
      }
    },
    [form, promoterId, showConfirm, referrersList?.referrers],
  );

  const handleGroupItemClick = useCallback(
    (ev: { key: string; id: string }) => {
      const group = referrerGroups?.find((item) => item.id === ev.id);
      if (ev.key === 'edit') {
        setIsGroupEditing(true);
        setGroupModalOpen(true);
        groupForm?.resetFields();
        groupForm.setFieldValue('name', group?.name);
        groupForm.setFieldValue('groupId', ev.id);
        setColorString(group?.colour);
      } else if (ev.key === 'remove') {
        showGroupConfirm(ev.id);
      }
    },
    [groupForm, showGroupConfirm, referrerGroups],
  );

  const showModal = () => {
    form?.resetFields();
    setOpen(true);
  };
  const handleOk = () => {
    setOpen(false);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setOpen(false);
    setIsEditing(false);
  };

  const showGroupModal = () => {
    groupForm?.resetFields();
    setGroupModalOpen(true);
  };
  const handleGroupModalOk = () => {
    setGroupModalOpen(false);
    setIsGroupEditing(false);
  };

  const handleGroupModalCancel = () => {
    setGroupModalOpen(false);
    setIsGroupEditing(false);
  };

  const changeColor = (color: Color) => {
    const hexColor = tinycolor(color.toRgb()).toHexString();
    setColorString(hexColor);
  };

  const showedElements =
    Number(referrersList?.referrers?.length) +
    (Number(referrersList?.pageNumber) - 1) * Number(requestFilter.PageSize);

  const options: SelectProps['options'] = referrerGroups?.map((item) => ({
    label: item.name as string,
    value: item.id,
    colour: item.colour,
  }));

  const [searchText, setSearchText] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [dropDownOpen, setDropDownOpen] = useState<boolean>(false);

  const filteredOptions = options?.filter((option) =>
    option?.label?.toString().toLowerCase().includes(searchText.toLowerCase()),
  );

  // const tagRender = (props: any) => {
  //   const { label, value, closable, onClose } = props;
  //   const option = options?.find((opt) => opt.value === value);
  //   return (
  //     <Tag
  //       color={option?.colour || 'blue'}
  //       closable={closable}
  //       onClose={onClose}
  //       style={{ marginRight: 4 }}>
  //       {label}
  //     </Tag>
  //   );
  // };

  const handleSubmit = useCallback(
    async (values) => {
      if (!promoterId) return;

      if (isEditing) {
        const editPayload = {
          email: values.email,
          promoterId,
          referrerLink: values.referrerLink,
          groupIds: selectedItems,
          id: values.id,
        };

        const editRes = await editReferrerList(editPayload);
        if ('data' in editRes) {
          message.success('Referral link Edited');
          setIsEditing(false);
          setOpen(false);
          form.resetFields();
          if (requestFilter?.PromoterId) {
            getReferrersList(requestFilter);
          }
        }
      } else {
        const payload = {
          email: values.email,
          promoterId,
          referrerLink: values.referrerLink,
          groupIds: selectedItems,
        };

        const res = await CreateReferrerLink(payload);

        if ('data' in res) {
          message.success('Referral link Created');
          setIsEditing(false);
          setOpen(false);
          form.resetFields();
          if (requestFilter?.PromoterId) {
            getReferrersList(requestFilter);
          }
        }
      }
    },
    [
      promoterId,
      isEditing,
      requestFilter,
      form,
      editReferrerList,
      selectedItems,
      getReferrersList,
      CreateReferrerLink,
    ],
  );

  const handlePageChange = useCallback((PageNumber: number) => {
    setRequestFilter((prevState) => ({
      ...prevState,
      PageNumber,
    }));
  }, []);

  const fetchChildData = async (key: string) => {
    setLoadingKeys((prev) => [...prev, key]); // Set loading state

    const res = await getReferrerItem({ id: key });

    if ('data' in res) {
      setChildData((prev) => ({ ...prev, [key]: res?.data })); // Store object
      setLoadingKeys((prev) => prev.filter((k) => k !== key)); // Remove loading state
    }
  };

  const handleExpand = (keys: string | string[]) => {
    if (type !== ReferrerEnum.ALL) {
      const activeKeys = Array.isArray(keys) ? keys : [keys];
      setExpandedKeys(activeKeys);

      activeKeys.forEach((key) => {
        if (!childData[key]) {
          fetchChildData(key);
        }
      });
    }
  };

  const handleCreateGroup = async (values) => {
    if (!isGroupEditing) {
      const payload: ReferrerGroupReqType = {
        name: values?.name,
        promoterId: promoterId || '',
        colour: colorString!,
      };

      const res = await createReferrersGroup(payload);

      if ('data' in res) {
        groupForm.resetFields();
        setGroupModalOpen(false);
        setIsGroupEditing(false);
        groupForm.resetFields();
        if (promoterId) {
          getReferrersGroups({ id: promoterId });
        }
        message.success('Successfully created group.');
      }
    } else {
      const payload: ReferrerGroupEditReqType = {
        name: values?.name,
        promoterId: promoterId || '',
        colour: colorString!,
        id: values?.groupId,
      };

      const res = await editReferrerGroup(payload);

      if ('data' in res) {
        groupForm.resetFields();
        setGroupModalOpen(false);
        setIsGroupEditing(false);
        groupForm.resetFields();
        if (promoterId) {
          getReferrersGroups({ id: promoterId });
        }
        message.success('Successfully created group.');
      }
    }
  };

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  useEffect(() => {
    if (currentUser) {
      setPromoterId(currentUser?.promoters[0].promoterId);
    }
  }, [currentUser]);

  useEffect(() => {
    if (promoterId) {
      getReferrersGroups({ id: promoterId });
    }
  }, [getReferrersGroups, promoterId]);

  useEffect(() => {
    if (requestFilter && requestFilter.PromoterId !== '') {
      getReferrersList(requestFilter);
    }
  }, [requestFilter, getReferrersList]);

  useEffect(() => {
    if (debouncedSearchEventName?.length >= 3) {
      setRequestFilter((prevState) => ({
        ...prevState,
        Term: debouncedSearchEventName,
      }));
    } else if (debouncedSearchEventName?.length === 0) {
      setRequestFilter((prevState) => ({
        ...prevState,
        Term: '',
      }));
    }
  }, [debouncedSearchEventName]);

  useEffect(() => {
    if (type) {
      setRequestFilter((prevState) => ({
        ...prevState,
        Type: type,
        PageNumber: 1,
      }));
    }
  }, [type]);

  useEffect(() => {
    if (promoterId) {
      setRequestFilter((prevState) => ({
        ...prevState,
        PromoterId: promoterId,
      }));
    }
  }, [promoterId]);

  const getItems = (panelStyle: CSSProperties): CollapseProps['items'] =>
    referrersList?.referrers?.map((referrer) => {
      const key = referrer?.id;
      return {
        key,
        label: (
          <div className={s.collapseItem}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {referrer?.user?.profilePic ? (
                <img
                  src={referrer?.user?.profilePic}
                  alt="saleschannel"
                  style={{ width: 40, height: 40, borderRadius: '50%' }}
                />
              ) : (
                <Icon name="user" color="black" size={20} />
              )}

              <span className={s.firstName}>{referrer?.user?.firstName}</span>
            </div>
            <div className={s.firstName}>{referrer?.user?.userEmail}</div>
            <div className={s.referralLink}>
              <Icon name="links" size={12} />
              ?p=
              {referrer?.referrerLink}
            </div>
            <div className={s.promoterName}>#{referrer?.promoter?.promoterName}</div>
            <Dropdown
              items={DROPDOWN_ITEMS()}
              placement="bottomRight"
              open={openedDropdown === referrer?.id}
              onOpenChange={(isOpen) => setOpenedDropdown(isOpen ? referrer?.id : '')}
              handleItemClick={(ev) => handleItemClick({ ...ev, id: referrer?.id })}>
              <div className={s.dropdownButton}>
                <Icon name="verticalDots" />
              </div>
            </Dropdown>
          </div>
        ), // Display custom label
        children: loadingKeys.includes(key) ? (
          <Spin style={{ marginLeft: 36, marginTop: 16, marginBottom: 16 }} />
        ) : childData[key] ? (
          childData[key]?.metrics?.map((item, key) => (
            <div key={key} className={s.childMetrics}>
              <p>
                <strong> {item?.eventName}</strong>
              </p>
              <p style={{ textAlign: 'center' }}>
                <strong style={{ color: '#187DFD' }}>
                  {' '}
                  {item?.qtyOfTicketsReferred} tickets sold
                </strong>
              </p>
              <p style={{ textAlign: 'right' }}>
                <strong>
                  {' '}
                  {item?.currency === 'eur' ? '€' : '£'}
                  {item?.valueOfTicketsReferred}
                </strong>
              </p>
            </div>
          ))
        ) : (
          <p>Click to load details</p>
        ), // Display relevant details
        style: panelStyle,
      };
    }) || [];

  return (
    <div className={s.wrapper}>
      <BackLink />
      <div className={s.header}>
        <Typography type="h2">Referral team</Typography>
        <Button className={s.addLinkButton} type="primary" size="large" onClick={showModal}>
          <Icon name="plus" />
          ADD REFERRAL
        </Button>
      </div>
      <div className={s.content}>
        <Input
          placeholder="Search by name, link or email"
          autoComplete="off"
          icon="search"
          name="term"
          onChange={(e) => setTerm(e.target.value)}
        />
        <Divider />
        <div className={s.selectWrapper}>
          <Select
            value={type}
            className={s.typeSelect}
            size="large"
            onChange={(value) => setType(value as ReferrerEnum)}
            defaultValue="All"
            options={[
              { label: 'All', value: ReferrerEnum.ALL },
              { label: 'Most Sales', value: ReferrerEnum.MOST_SALES },
            ]}
            placeholder="Choose event type"
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              width: '100%',
            }}>
            <Typography type="label">Promoter:</Typography>
            <Select
              value={promoterId}
              className={s.promoterSelect}
              size="large"
              onChange={(value) => setPromoterId(value)}
              defaultValue={currentUser?.promoters[0].promoterId}
              options={promoterOptions}
              placeholder="Choose promoter"
            />
          </div>
        </div>
        {isFetching ? (
          <EventsLoadingState size={250} />
        ) : referrersList?.referrers?.length && referrersList?.referrers?.length > 0 ? (
          <Collapse
            bordered={false}
            expandIconPosition="end"
            activeKey={expandedKeys}
            onChange={handleExpand}
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            className={s.collapseContent}
            style={{
              background: token.colorBgContainer,
            }}
            items={getItems(panelStyle)}
          />
        ) : (
          <Empty description="No referrers" />
        )}
        {showedElements > 0 && (
          <Pagination
            totalElements={referrersList?.totalRecords}
            showedElements={showedElements}
            currentPage={requestFilter?.PageNumber || 0}
            total={referrersList?.totalPages}
            handlePageChange={handlePageChange}
            defaultPageSize={10}
            pageSizeOptions={[
              { value: 10, label: '10' },
              { value: 20, label: '20' },
              { value: 30, label: '30' },
            ]}
            handleSizeChange={(size) =>
              setRequestFilter((prevValue) => ({ ...prevValue, PageSize: size, PageNumber: 1 }))
            }
          />
        )}
      </div>
      <Modal
        open={open}
        onOk={handleOk}
        handleClose={handleCancel}
        closable={true}
        footer={[]}
        maskClosable={false}>
        <Modal.Header>
          <Typography type="h3" weight={700}>
            {isEditing === true ? 'EDIT' : 'ADD'} referral link
          </Typography>
          {/* <div style={{ marginTop: 16, marginBottom: 16 }}>
            <DownloadSwitcher value={isPDF} onChange={setIsPDF} />
          </div> */}
        </Modal.Header>
        <Form className={s.form} onFinish={(values) => handleSubmit(values)} form={form}>
          <div>
            <Form.Item name="promoterId" style={{ display: 'none' }}>
              <Input value={promoterId} name="promoterId" />
            </Form.Item>
            <Form.Item name="id" style={{ display: 'none' }}>
              <Input name="id" />
            </Form.Item>
          </div>
          <div className={s.itemWrapper}>
            <Label>E-mail of the referrer</Label>
            <Form.Item name="email" validateFirst rules={[formRules.required, formRules.email]}>
              <Input name="email" autoComplete="off" placeholder="Enter E-mail" />
            </Form.Item>
          </div>
          <div className={s.itemWrapper}>
            <Label>Tag</Label>
            <Form.Item
              name="referrerLink"
              validateFirst
              rules={[formRules.required, formRules.referrerLink]}>
              <Input name="referrerLink" autoComplete="off" placeholder="Add tag" />
            </Form.Item>
          </div>
          <div style={{ marginTop: 8 }}>
            {selectedItems.map((item) => (
              <Tag
                key={item}
                className={s.tag}
                closable
                onClose={() => handleChange(selectedItems.filter((i) => i !== item))}>
                {options?.find((opt) => opt.value === item)?.label}
              </Tag>
            ))}
          </div>
          <div className={s.itemWrapper}>
            <Label>In teams</Label>
            {/* <Form.Item name="groupIds" validateFirst>
              <Select
                mode="multiple"
                size="large"
                style={{ width: '100%' }}
                value={selectedItems}
                onChange={handleChange}
                dropdownRender={() => (
                  <div>
                    {options?.map((option) => (
                      <Checkbox
                        key={option.value}
                        checked={selectedItems.includes(option?.value as string)}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...selectedItems, option.value]
                            : selectedItems.filter((item) => item !== option.value);
                          setSelectedItems(newSelected as string[]);
                        }}
                        style={{ display: 'flex', alignItems: 'center', padding: '5px 10px' }}>
                        {option.label}
                      </Checkbox>
                    ))}
                  </div>
                )}
              />
            </Form.Item> */}

            <Button
              type="primary"
              className={s.memberButton}
              onClick={() => setDropDownOpen((prev) => !prev)}>
              <Icon name="plus" />
              ADD MEMBER TO A GROUP
            </Button>
            {dropDownOpen === true && (
              <div
                style={{
                  padding: '8px 12px',
                  width: '250px',
                  borderRadius: 8,
                  border: '1px solid #e7e9f1',
                  marginTop: 8,
                }}>
                {/* Search Input */}
                <div>Add to group:</div>
                <Input
                  className={s.groupSearch}
                  name="search"
                  placeholder="Search"
                  suffix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ marginBottom: 8 }}
                />
                {/* Filtered Checkbox List */}
                {filteredOptions?.map((option) => (
                  <Checkbox
                    key={option.value}
                    checked={selectedItems.includes(option.value as string)}
                    onChange={(e) => handleCheckboxChange(e.target.checked, option.value as string)}
                    style={{ display: 'flex', alignItems: 'center', padding: '5px 0' }}>
                    {option.label}
                  </Checkbox>
                ))}
                <AntBtn
                  size="large"
                  type="link"
                  className={s.createNewGroup}
                  onClick={showGroupModal}>
                  Create new group
                </AntBtn>
              </div>
            )}
          </div>
          <div></div>
          {referrerGroups?.map((item, index) => (
            <div key={index} className={s.groupTable}>
              {item.name}
              <Tag color={item.colour}>{item.colour}</Tag>
              <Dropdown
                items={GROUP_DROPDOWN_ITEMS()}
                placement="bottomRight"
                open={openedGroupDropdown === item?.id}
                onOpenChange={(isOpen) => setOpenedGroupDropdown(isOpen ? item?.id : '')}
                handleItemClick={(ev) => handleGroupItemClick({ ...ev, id: item?.id })}>
                <div className={s.dropdownButton}>
                  <Icon name="verticalDots" />
                </div>
              </Dropdown>
            </div>
          ))}
          <Divider style={{ margin: 0 }} />
          <div key="batchAction" className={s.modalActions}>
            <Button
              key="ok"
              htmlType="submit"
              type="primary"
              size="large"
              loading={isSending || isEditingReferrerList}
              block
              className={s.addLink}
              onClick={() => handleSubmit}>
              {isEditing === true ? 'EDIT' : 'ADD'} REFERAL LINK
            </Button>
            <Button
              key="back"
              size="large"
              className={s.cancel}
              type="link"
              block
              onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </Form>
      </Modal>
      <Modal
        open={groupModalOpen}
        onOk={handleGroupModalOk}
        handleClose={handleGroupModalCancel}
        closable={true}
        footer={[]}
        maskClosable={false}>
        {' '}
        <Modal.Header>
          <div style={{ marginTop: 24 }}></div>
          <Typography type="h3" weight={700}>
            {isGroupEditing ? 'Edit' : 'Create'} Group
          </Typography>
          <div style={{ marginTop: 24 }}></div>
        </Modal.Header>
        <Form className={s.fullWidth} onFinish={(data) => handleCreateGroup(data)} form={groupForm}>
          <div className={s.inputBlock}>
            <div className={s.itemWrapper}>
              <Label>Enter a group name</Label>
              <Form.Item name="groupId" style={{ display: 'none' }}>
                <Input name="groupId" />
              </Form.Item>
              <Form.Item name="name" validateFirst rules={[formRules.required]}>
                <Input name="name" autoComplete="off" placeholder="Enter name" />
              </Form.Item>
              <ColorPicker
                style={{ marginTop: 24 }}
                value={colorString}
                onChange={changeColor}
                showText={(color) => <span>Team Color ({color.toHexString()})</span>}
              />
            </div>
            <Divider />
            <div key="batchAction" className={s.GROUPmodalActions}>
              <Button
                key="ok"
                htmlType="submit"
                type="primary"
                size="large"
                block
                loading={isGrouping || isEditingGroup}
                className={s.addLink}>
                {isGroupEditing ? 'Edit' : 'Create'} GROUP
              </Button>
              <Button
                key="back"
                size="large"
                className={s.cancel}
                type="link"
                block
                onClick={handleGroupModalCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ReferralTeam;
